import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Row, Col, Button, Table } from 'reactstrap';
import Swal from 'sweetalert2';
import { useCookies } from 'react-cookie';
import CreatableSelect from 'react-select/creatable';
import Sidebar from '../../Sidebar';
import FormPageLayout from '../../components/FormPageLayout';
import FloatingControls from '../../components/FloatingControls';
import LoadingOverlay from '../../components/LoadingOverlay';
import ErrorToast from '../../ErrorToast';
import { displayError } from '../../helpers';
import { getCurrentDateDisplay, formatDateForApi } from '../../utils/DateUtils';
import { getUserAllowedPlants } from '../../services/PlantServices';
import { getAllCustomerMain } from '../../services/CustomerMainServices';
import { getAllCustomerSites, getCustomerSiteDetails } from '../../services/CustomerSiteServices';
import {
    createConcreteEstimator,
    updateConcreteEstimator,
    getConcreteEstimatorDetails,
    getEstimateNumber,
    getDieselRate,
    printConcreteEstimator,
    getPincodeLookup,
    getPincodeLocations,
    getPincodeStreets,
    getLocationDistance,
} from '../../services/ConcreteEstimatorServices';
import { getAllProducts } from '../../services/ProductServices';
import { navigateToQuotationFromEstimate, navigateToQuotationFromEstimateData } from '../../utils/convertEstimateToQuotation';
import {
    SHAPE_TYPES,
    PROJECT_TYPES,
    CONCRETE_GRADES,
    LENGTH_UNITS,
    getDefaultDimensions,
    getShapeFieldLabels,
    ensureDimensionUnits,
    computeEstimatorLine,
    computeSummaryTotals,
    getSummaryLineQty,
    getSummaryLineRate,
    sumLineItems,
    formatMeasurementSummary,
    summaryFieldsFromLine,
    summaryOverridesForDimensions,
} from '../../utils/concreteEstimatorCalc';
import EstimatorShapeDiagram from '../../components/EstimatorShapeDiagram';
import ConcreteEstimatorRateWorking from '../../components/ConcreteEstimatorRateWorking';
import EstimatorLocationMapModal from '../../components/EstimatorLocationMapModal';
import { formatCurrency, formatRatePerM3 } from '../../utils/concreteEstimatorRateCalc';
import { openPdfResponse } from '../../utils/openPdfInApp';
import { areasForPincode, streetsForPincodeArea } from '../../utils/pincodeLocalities';
import { ESTIMATE_PDF_TYPES, chooseEstimatePdfType, estimatePdfTitle } from '../../utils/concreteEstimatorPdf';
import '../../button.css';

const EMPTY_SITE_LOCATION = {
    site_pincode: '',
    site_location: '',
    site_address: '',
    distance_one_way_km: '',
    distance_round_trip_km: '',
};

const PINCODE_PATTERN = /^[1-9][0-9]{5}$/;

function formatSiteAddress(site = {}) {
    return [site.address_1, site.address_2, site.address_3].filter(Boolean).join(', ');
}

function createdOption(label) {
    const name = (label || '').trim();
    if (!name) return null;
    return { value: name, label: name, __isNew__: true };
}

function mapSiteOption(site = {}) {
    return {
        value: site.id,
        label: site.project_name || site.name || '',
        pincode: String(site.pincode || ''),
        address: formatSiteAddress(site),
        radius_distance: Number(site.radius_distance) || 0,
        customer_id: site.customer?.id ? String(site.customer.id) : '',
        customer_name: site.customer?.name || '',
    };
}

function distanceFields(oneWay) {
    const km = Number(oneWay);
    if (!Number.isFinite(km) || km < 0) {
        return { distance_one_way_km: '', distance_round_trip_km: '' };
    }
    return {
        distance_one_way_km: String(km),
        distance_round_trip_km: String((km * 2).toFixed(2)),
    };
}

function mergeNamedOptions(incoming, previous) {
    const seen = new Set();
    const options = [];
    const add = (opt) => {
        const labelKey = (opt.label || '').toLowerCase();
        const nameKey = (opt.name || '').toLowerCase();
        if (!labelKey || seen.has(labelKey) || (nameKey && seen.has(nameKey))) {
            return;
        }
        seen.add(labelKey);
        if (nameKey) {
            seen.add(nameKey);
        }
        options.push(opt);
    };
    (incoming || []).forEach(add);
    (previous || []).forEach(add);
    options.sort((a, b) => a.label.localeCompare(b.label));
    return options;
}

function mapPostalLocation(loc = {}) {
    const name = loc.name || loc.Name || '';
    const district = loc.district || loc.District || '';
    const state = loc.state || loc.State || '';
    const label = loc.label || [name, district, state].filter(Boolean).join(', ');
    return {
        value: label,
        label,
        name,
    };
}

async function fetchPostalAreasFromIndiaPost(pincode) {
    const response = await fetch(`https://api.postalpincode.in/pincode/${pincode}`);
    const data = await response.json();
    if (!data?.[0] || data[0].Status !== 'Success') {
        return [];
    }
    const seen = new Set();
    return (data[0].PostOffice || []).reduce((list, office) => {
        const mapped = mapPostalLocation(office);
        const key = (mapped.name || mapped.label).toLowerCase();
        if (!mapped.label || seen.has(key)) {
            return list;
        }
        seen.add(key);
        list.push(mapped);
        return list;
    }, []);
}

function siteLocationFromDetails(site = {}) {
    const oneWay = Number(site.radius_distance) || 0;
    return {
        project_name: site.project_name || '',
        site_pincode: site.pincode || '',
        site_location: '',
        site_address: formatSiteAddress(site),
        ...distanceFields(oneWay || ''),
    };
}

const emptyLine = (lineNo = 1, defaults = {}) => ({
    line_no: lineNo,
    shape_type: 'slab_wall_footing',
    item_name: '',
    quantity: 1,
    concrete_grade: defaults.concrete_grade || 'M20',
    wastage_percent: defaults.wastage_percent ?? 3,
    dimensions: getDefaultDimensions('slab_wall_footing'),
    manual_qty_m3: '',
    manual_rate_per_m3: '',
    user_remarks: '',
});

function ConcreteEstimatorForm({ editId }) {
    const [cookies] = useCookies(['myToken']);
    const navigate = useNavigate();
    const [loading, setLoading] = useState(Boolean(editId));
    const [plants, setPlants] = useState([]);
    const [plantPincodes, setPlantPincodes] = useState({});
    const [customers, setCustomers] = useState([]);
    const [allSites, setAllSites] = useState([]);
    const [selectedCustomerOption, setSelectedCustomerOption] = useState(null);
    const [selectedSiteOption, setSelectedSiteOption] = useState(null);
    const [locationOptions, setLocationOptions] = useState([]);
    const [streetOptions, setStreetOptions] = useState([]);
    const [streetsLoading, setStreetsLoading] = useState(false);
    const [plantCoords, setPlantCoords] = useState(null);
    const [siteCoords, setSiteCoords] = useState(null);
    const [mapOpen, setMapOpen] = useState(false);
    const skipPincodeLookupRef = useRef(false);
    const locationOptionsRef = useRef([]);
    const streetOptionsRef = useRef([]);
    const streetsLoadedKeyRef = useRef('');
    const [inputs, setInputs] = useState({
        plant_id: '',
        estimate_no: '',
        prefix: '',
        estimate_date: getCurrentDateDisplay(),
        project_name: '',
        project_type: 'individual_house',
        customer_id: '',
        consignee_id: '',
        site_pincode: '',
        site_location: '',
        site_address: '',
        distance_one_way_km: '',
        distance_round_trip_km: '',
        current_diesel_rate: '',
        grn_diesel_rate: '',
        client_requirement: 'qty',
        user_remarks: '',
    });
    const [lines, setLines] = useState([emptyLine()]);
    const [rateWorking, setRateWorking] = useState(null);
    const [initialRateSheet, setInitialRateSheet] = useState(null);
    const [dieselRateTouched, setDieselRateTouched] = useState(false);

    useEffect(() => {
        getUserAllowedPlants(cookies).then((res) => {
            const opts = (res.plant_list || []).filter((p) => p.status).map((p) => ({
                value: p.id,
                label: p.plant_alias,
            }));
            const pins = {};
            (res.plant_list || []).forEach((p) => {
                pins[String(p.id)] = String(p.pincode || '');
            });
            setPlants(opts);
            setPlantPincodes(pins);
            if (!editId && opts.length === 1) {
                setInputs((v) => ({ ...v, plant_id: String(opts[0].value) }));
            }
        });
        getAllCustomerMain(cookies).then((res) => {
            setCustomers(
                (res.customer_list || [])
                    .filter((c) => c.status)
                    .map((c) => ({ value: c.id, label: c.name })),
            );
        });
        getAllCustomerSites(cookies).then((res) => {
            setAllSites(
                (res.consignee_list || [])
                    .filter((s) => s.status)
                    .map(mapSiteOption),
            );
        });
    }, [cookies, editId]);

    useEffect(() => {
        if (!editId) return;
        setLoading(true);
        getConcreteEstimatorDetails(cookies, editId)
            .then((data) => {
                setInputs({
                    plant_id: String(data.plant_id || ''),
                    estimate_no: data.estimate_no,
                    prefix: data.prefix,
                    estimate_date: data.estimate_date,
                    project_name: data.project_name,
                    project_type: data.project_type,
                    customer_id: data.customer_id ? String(data.customer_id) : '',
                    consignee_id: data.consignee_id ? String(data.consignee_id) : '',
                    site_pincode: data.site_pincode || '',
                    site_location: data.site_location || '',
                    site_address: data.site_address || '',
                    distance_one_way_km: data.distance_one_way_km != null && data.distance_one_way_km !== ''
                        ? String(data.distance_one_way_km)
                        : '',
                    distance_round_trip_km: data.distance_round_trip_km != null && data.distance_round_trip_km !== ''
                        ? String(data.distance_round_trip_km)
                        : '',
                    current_diesel_rate: data.current_diesel_rate != null && data.current_diesel_rate !== '' && Number(data.current_diesel_rate) > 0
                        ? String(data.current_diesel_rate)
                        : (data.grn_diesel_rate != null && data.grn_diesel_rate !== '' && Number(data.grn_diesel_rate) > 0
                            ? String(data.grn_diesel_rate)
                            : ''),
                    grn_diesel_rate: data.grn_diesel_rate != null && data.grn_diesel_rate !== ''
                        ? String(data.grn_diesel_rate)
                        : '',
                    client_requirement: data.client_requirement || 'qty',
                    user_remarks: data.user_remarks || '',
                });
                setDieselRateTouched(
                    data.current_diesel_rate != null
                    && data.current_diesel_rate !== ''
                    && Number(data.current_diesel_rate) > 0,
                );
                setLines(
                    (data.lines || []).map((l, i) => {
                        const dimensions = ensureDimensionUnits(l.shape_type, l.dimensions || getDefaultDimensions(l.shape_type));
                        const quantity = l.quantity ?? 1;
                        const summaryFields = summaryFieldsFromLine({ ...l, dimensions });
                        return {
                            ...l,
                            line_no: l.line_no || i + 1,
                            concrete_grade: l.concrete_grade || data.concrete_grade || 'M20',
                            wastage_percent: l.wastage_percent ?? data.wastage_percent ?? 3,
                            dimensions,
                            manual_qty_m3: summaryFields.manual_qty_m3,
                            manual_rate_per_m3: summaryFields.manual_rate_per_m3
                                || (data.rate_working?.rate_per_m3 ? String(data.rate_working.rate_per_m3) : ''),
                        };
                    }),
                );
                if (data.rate_working?.materials?.length) {
                    setInitialRateSheet(data.rate_working);
                    setRateWorking(data.rate_working);
                }
            })
            .catch((err) => displayError(err?.response?.data ?? err.message, 'Load failed'))
            .finally(() => setLoading(false));
    }, [cookies, editId]);

    useEffect(() => {
        if (editId || !inputs.plant_id || !inputs.estimate_date) return;
        getEstimateNumber(cookies, inputs.plant_id, formatDateForApi(inputs.estimate_date))
            .then((res) => {
                setInputs((v) => ({
                    ...v,
                    estimate_no: res.estimate_no,
                    prefix: res.prefix,
                }));
            })
            .catch((err) => {
                const msg = err?.response?.data?.message
                    || err?.response?.data?.detail
                    || err?.message
                    || 'Could not load estimate number';
                displayError(err?.response?.data ?? msg, 'Estimate number');
            });
    }, [cookies, editId, inputs.plant_id, inputs.estimate_date]);

    useEffect(() => {
        if (!inputs.plant_id || dieselRateTouched) return;
        getDieselRate(cookies, inputs.plant_id)
            .then((res) => {
                const grnRate = Number(res.grn_diesel_rate) || 0;
                setInputs((v) => {
                    const hasCurrent = v.current_diesel_rate !== '' && Number(v.current_diesel_rate) > 0;
                    return {
                        ...v,
                        grn_diesel_rate: grnRate ? String(grnRate) : '',
                        current_diesel_rate: hasCurrent ? v.current_diesel_rate : (grnRate ? String(grnRate) : ''),
                    };
                });
            })
            .catch(() => {});
    }, [cookies, dieselRateTouched, inputs.plant_id]);

    const sites = useMemo(() => {
        if (!inputs.customer_id) {
            return allSites;
        }
        return allSites.filter((site) => String(site.customer_id) === String(inputs.customer_id));
    }, [allSites, inputs.customer_id]);

    useEffect(() => {
        if (inputs.customer_id) {
            const match = customers.find((c) => String(c.value) === String(inputs.customer_id));
            if (match) {
                setSelectedCustomerOption(match);
                return;
            }
        }
        setSelectedCustomerOption((current) => {
            if (current?.__isNew__) {
                return current;
            }
            if (!inputs.customer_id && inputs.project_name) {
                return createdOption(inputs.project_name);
            }
            return current;
        });
    }, [customers, inputs.customer_id, inputs.project_name]);

    useEffect(() => {
        if (inputs.consignee_id) {
            const match = allSites.find((s) => String(s.value) === String(inputs.consignee_id));
            if (match) {
                setSelectedSiteOption(match);
                return;
            }
        }
        setSelectedSiteOption((current) => {
            if (current?.__isNew__) {
                return current;
            }
            if (!inputs.consignee_id && inputs.project_name) {
                return createdOption(inputs.project_name);
            }
            return current;
        });
    }, [allSites, inputs.consignee_id, inputs.project_name]);

    const applySiteLocation = (siteId) => {
        if (!siteId) {
            setInputs((v) => ({ ...v, ...EMPTY_SITE_LOCATION }));
            return;
        }
        const cached = allSites.find((site) => String(site.value) === String(siteId));
        if (cached) {
            skipPincodeLookupRef.current = false;
            setInputs((v) => ({
                ...v,
                project_name: cached.label,
                site_pincode: cached.pincode || v.site_pincode,
                site_location: '',
                site_address: cached.address || v.site_address,
                ...(cached.radius_distance > 0 ? distanceFields(cached.radius_distance) : {}),
            }));
            return;
        }
        getCustomerSiteDetails(cookies, siteId)
            .then((site) => {
                skipPincodeLookupRef.current = false;
                setInputs((v) => ({ ...v, ...siteLocationFromDetails(site) }));
            })
            .catch(() => {});
    };

    const onCustomerSelect = (selected) => {
        setSelectedCustomerOption(selected);
        const name = selected?.label?.trim() || '';
        const siteFromCustomer = name ? createdOption(name) : null;
        setSelectedSiteOption(siteFromCustomer);
        setLocationOptions([]);
        setStreetOptions([]);
        locationOptionsRef.current = [];
        streetOptionsRef.current = [];
        streetsLoadedKeyRef.current = '';
        setSiteCoords(null);
        setInputs((v) => ({
            ...v,
            customer_id: selected && !selected.__isNew__ ? String(selected.value) : '',
            consignee_id: '',
            project_name: name,
            ...EMPTY_SITE_LOCATION,
        }));
    };

    const onSiteSelect = (selected) => {
        setSelectedSiteOption(selected);
        const isExisting = selected && !selected.__isNew__;
        const siteId = isExisting ? String(selected.value) : '';
        const name = selected?.label?.trim() || '';
        if (isExisting && selected.customer_id) {
            const customerMatch = customers.find((c) => String(c.value) === String(selected.customer_id));
            setSelectedCustomerOption(customerMatch || {
                value: selected.customer_id,
                label: selected.customer_name || name,
            });
        }
        setInputs((v) => ({
            ...v,
            consignee_id: siteId,
            project_name: name,
            customer_id: isExisting && selected.customer_id ? String(selected.customer_id) : v.customer_id,
            ...(siteId ? {} : { ...EMPTY_SITE_LOCATION, project_name: name }),
        }));
        if (siteId) {
            applySiteLocation(siteId);
        }
    };

    const applyDistanceResult = (res, locationLabel) => {
        if (res?.plant) {
            setPlantCoords(res.plant);
        }
        if (res?.site) {
            setSiteCoords(res.site);
        }
        setInputs((v) => {
            const next = {
                ...v,
                site_location: locationLabel || res?.site_location || v.site_location,
            };
            if (res?.distance_one_way_km != null && res.distance_one_way_km !== '') {
                Object.assign(next, distanceFields(res.distance_one_way_km));
            }
            return next;
        });
    };

    const applyPostalAreas = (pincode, offices) => {
        const incoming = [
            ...areasForPincode(pincode),
            ...(offices || []).map(mapPostalLocation).filter((opt) => opt.label),
        ];
        const merged = mergeNamedOptions(incoming, locationOptionsRef.current);
        locationOptionsRef.current = merged;
        setLocationOptions(merged);
        setInputs((v) => {
            if (String(v.site_pincode || '').trim() !== String(pincode)) {
                return v;
            }
            const keepCurrent = Boolean(v.site_location);
            return {
                ...v,
                site_location: keepCurrent ? v.site_location : (merged[0]?.value || ''),
            };
        });
        return merged;
    };

    const applyStreets = (pincode, locality, streets) => {
        const incoming = [
            ...streetsForPincodeArea(pincode, locality),
            ...(streets || []).map((item) => ({
                value: item.label || item.name,
                label: item.label || item.name,
                name: item.name || item.label,
            })).filter((opt) => opt.label),
        ];
        const merged = mergeNamedOptions(incoming, streetOptionsRef.current);
        streetOptionsRef.current = merged;
        setStreetOptions(merged);
        setInputs((v) => {
            if (String(v.site_pincode || '').trim() !== String(pincode)) {
                return v;
            }
            const keepCurrent = Boolean(v.site_address);
            return {
                ...v,
                site_address: keepCurrent ? v.site_address : (merged[0]?.value || ''),
            };
        });
        return merged;
    };

    const loadPincodeAreas = useCallback((pincode) => {
        fetchPostalAreasFromIndiaPost(pincode)
            .then((offices) => {
                if (offices.length) {
                    applyPostalAreas(pincode, offices);
                }
            })
            .catch(() => {});
        getPincodeLocations(cookies, pincode)
            .then((res) => {
                if (res?.locations?.length) {
                    applyPostalAreas(pincode, res.locations);
                }
            })
            .catch(() => (
                fetchPostalAreasFromIndiaPost(pincode)
                    .then((offices) => applyPostalAreas(pincode, offices))
                    .catch(() => {})
            ));
    }, [cookies]);

    const loadAreaStreets = useCallback((pincode, locality) => {
        if (!pincode || !locality) {
            streetOptionsRef.current = [];
            setStreetOptions([]);
            setStreetsLoading(false);
            return;
        }
        const curated = streetsForPincodeArea(pincode, locality);
        streetOptionsRef.current = [];
        if (curated.length) {
            applyStreets(pincode, locality, []);
            setStreetsLoading(false);
        } else {
            streetOptionsRef.current = [];
            setStreetOptions([]);
            setStreetsLoading(true);
        }
        getPincodeStreets(cookies, pincode, locality)
            .then((res) => {
                if (res?.streets?.length) {
                    applyStreets(pincode, locality, res.streets);
                }
            })
            .catch(() => {})
            .finally(() => setStreetsLoading(false));
    }, [cookies]);

    const lookupPincodeDistance = useCallback((pincode) => {
        const plantPin = plantPincodes[String(inputs.plant_id)] || '';
        getPincodeLookup(cookies, {
            pincode,
            from_pincode: plantPin,
            plant_id: inputs.plant_id,
        })
            .then((res) => {
                if (res.plant) {
                    setPlantCoords(res.plant);
                }
                if (res.site) {
                    setSiteCoords(res.site);
                }
                setInputs((v) => {
                    if (String(v.site_pincode || '').trim() !== String(pincode)) {
                        return v;
                    }
                    const next = { ...v };
                    if (res.distance_one_way_km != null && res.distance_one_way_km !== '') {
                        Object.assign(next, distanceFields(res.distance_one_way_km));
                    }
                    return next;
                });
            })
            .catch(() => {});
    }, [cookies, inputs.plant_id, plantPincodes]);

    useEffect(() => {
        const pin = String(inputs.site_pincode || '').trim();
        if (!PINCODE_PATTERN.test(pin)) {
            locationOptionsRef.current = [];
            streetOptionsRef.current = [];
            setLocationOptions([]);
            setStreetOptions([]);
            return undefined;
        }
        if (skipPincodeLookupRef.current) {
            skipPincodeLookupRef.current = false;
            return undefined;
        }
        locationOptionsRef.current = [];
        streetOptionsRef.current = [];
        streetsLoadedKeyRef.current = '';
        setLocationOptions([]);
        setStreetOptions([]);
        const timer = window.setTimeout(() => {
            const curatedAreas = areasForPincode(pin);
            if (curatedAreas.length) {
                applyPostalAreas(pin, []);
            }
            loadPincodeAreas(pin);
            lookupPincodeDistance(pin);
        }, 250);
        return () => window.clearTimeout(timer);
    }, [inputs.site_pincode, loadPincodeAreas, lookupPincodeDistance]);

    useEffect(() => {
        const pin = String(inputs.site_pincode || '').trim();
        const loc = String(inputs.site_location || '').trim();
        if (!PINCODE_PATTERN.test(pin) || !loc) {
            streetsLoadedKeyRef.current = '';
            return undefined;
        }
        const match = locationOptions.find((opt) => opt.value === loc);
        const locality = match?.name || loc;
        const key = `${pin}|${locality.toLowerCase()}`;
        if (streetsLoadedKeyRef.current === key) {
            return undefined;
        }
        streetsLoadedKeyRef.current = key;
        loadAreaStreets(pin, locality);
        return undefined;
    }, [inputs.site_pincode, inputs.site_location, locationOptions, loadAreaStreets]);

    const handleOneWayKm = (e) => {
        const raw = e.target.value;
        const oneWay = raw === '' ? '' : Number(raw);
        setInputs((v) => ({
            ...v,
            distance_one_way_km: raw,
            distance_round_trip_km: oneWay === '' || !Number.isFinite(oneWay)
                ? ''
                : String((oneWay * 2).toFixed(2)),
        }));
    };

    const computedLines = useMemo(
        () => lines.map((line) => computeEstimatorLine(line)),
        [lines],
    );

    const totals = useMemo(() => sumLineItems(computedLines), [computedLines]);

    const defaultRatePerM3 = Number(rateWorking?.rate_per_m3) || 0;
    const summaryTotals = useMemo(
        () => computeSummaryTotals(lines, computedLines, defaultRatePerM3),
        [lines, computedLines, defaultRatePerM3],
    );

    const handleRateWorkingChange = useCallback((sheet) => {
        setRateWorking(sheet);
        if (sheet.diesel_rate != null && sheet.diesel_rate !== '') {
            setInputs((v) => ({
                ...v,
                current_diesel_rate: String(sheet.diesel_rate),
            }));
        }
    }, []);

    const distanceRoundTripKm = inputs.distance_round_trip_km === ''
        ? 0
        : Number(inputs.distance_round_trip_km) || 0;

    const handleInput = (e) => {
        const { name, value } = e.target;
        setInputs((v) => ({ ...v, [name]: value }));
    };

    const handlePincodeChange = (e) => {
        const value = e.target.value.replace(/\D/g, '').slice(0, 6);
        if (value.length < 6) {
            locationOptionsRef.current = [];
            streetOptionsRef.current = [];
            setLocationOptions([]);
            setStreetOptions([]);
            setSiteCoords(null);
        }
        setInputs((v) => ({
            ...v,
            site_pincode: value,
            site_location: '',
            site_address: '',
        }));
    };

    const selectedLocationName = () => {
        const label = String(inputs.site_location || '').trim();
        const match = locationOptions.find((opt) => opt.value === label);
        return match?.name || label;
    };

    const onSiteLocationSelect = (event) => {
        const label = event.target.value;
        const match = locationOptions.find((opt) => opt.value === label);
        setInputs((v) => ({ ...v, site_location: label, site_address: '' }));
        streetOptionsRef.current = [];
        streetsLoadedKeyRef.current = '';
        setStreetOptions([]);
        if (!label || !PINCODE_PATTERN.test(String(inputs.site_pincode || '').trim())) {
            return;
        }
        getLocationDistance(cookies, {
            plant_id: inputs.plant_id,
            from_pincode: plantPincodes[String(inputs.plant_id)] || '',
            pincode: inputs.site_pincode,
            query: match?.name || label,
        })
            .then((res) => applyDistanceResult(res, label))
            .catch(() => {});
    };

    const onSiteAddressSelect = (event) => {
        const street = event.target.value;
        setInputs((v) => ({ ...v, site_address: street }));
        if (!street || !PINCODE_PATTERN.test(String(inputs.site_pincode || '').trim())) {
            return;
        }
        const locality = selectedLocationName();
        getLocationDistance(cookies, {
            plant_id: inputs.plant_id,
            from_pincode: plantPincodes[String(inputs.plant_id)] || '',
            pincode: inputs.site_pincode,
            query: [street, locality].filter(Boolean).join(', '),
        })
            .then((res) => applyDistanceResult(res, locality || inputs.site_location))
            .catch(() => {});
    };

    const openLocationMap = () => {
        const pin = String(inputs.site_pincode || '').trim();
        if (!PINCODE_PATTERN.test(pin) && !siteCoords) {
            Swal.fire('Pincode needed', 'Enter a valid 6-digit pincode first, then choose the site location on the map.', 'info');
            return;
        }
        setMapOpen(true);
    };

    const closeLocationMap = () => setMapOpen(false);

    const handleMapSelect = (selection) => {
        setMapOpen(false);
        getLocationDistance(cookies, {
            plant_id: inputs.plant_id,
            from_pincode: plantPincodes[String(inputs.plant_id)] || '',
            pincode: inputs.site_pincode,
            lat: selection.lat,
            lon: selection.lon,
            query: selection.site_location || inputs.site_location,
        })
            .then((res) => {
                const label = res.site_location || selection.site_location || inputs.site_location;
                if (label && !locationOptions.some((opt) => opt.value === label)) {
                    setLocationOptions((current) => [{ value: label, label, name: label }, ...current]);
                }
                applyDistanceResult(res, label);
            })
            .catch(() => {
                if (selection.site_location) {
                    setInputs((v) => ({ ...v, site_location: selection.site_location }));
                }
            });
    };

    const updateLine = (index, patch) => {
        setLines((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
    };

    const updateLineManualQty = (index, value) => {
        updateLine(index, { manual_qty_m3: value });
    };

    const updateLineManualRate = (index, value) => {
        updateLine(index, { manual_rate_per_m3: value });
    };

    const updateLineDim = (index, key, value) => {
        setLines((prev) => prev.map((row, i) => (
            i === index
                ? { ...row, dimensions: { ...row.dimensions, [key]: value } }
                : row
        )));
    };

    const changeShape = (index, shapeType) => {
        updateLine(index, {
            shape_type: shapeType,
            dimensions: getDefaultDimensions(shapeType),
        });
    };

    const clearLine = (index) => {
        const row = lines[index] || {};
        const shapeType = row.shape_type || 'slab_wall_footing';
        updateLine(index, {
            item_name: '',
            quantity: 1,
            concrete_grade: row.concrete_grade || 'M20',
            wastage_percent: row.wastage_percent ?? 3,
            dimensions: getDefaultDimensions(shapeType),
        });
    };

    const addLine = () => {
        setLines((prev) => {
            const last = prev[prev.length - 1];
            return [...prev, emptyLine(prev.length + 1, {
                concrete_grade: last?.concrete_grade,
                wastage_percent: last?.wastage_percent,
            })];
        });
    };

    const removeLine = (index) => {
        setLines((prev) => {
            if (prev.length <= 1) return prev;
            return prev.filter((_, i) => i !== index).map((row, i) => ({ ...row, line_no: i + 1 }));
        });
    };

    const handleConvertToQuotation = () => {
        const estimatePayload = {
            id: editId,
            prefix: inputs.prefix,
            estimate_no: inputs.estimate_no,
            project_name: inputs.project_name,
            project_type: inputs.project_type,
            concrete_grade: computedLines[0]?.concrete_grade || 'M20',
            total_volume_m3: totals.total_volume_m3,
            quoted_rate_per_m3: rateWorking?.rate_per_m3 || 0,
            rate_working: rateWorking,
            user_remarks: inputs.user_remarks,
            customer_id: inputs.customer_id && !Number.isNaN(Number(inputs.customer_id)) ? Number(inputs.customer_id) : null,
            consignee_id: inputs.consignee_id && !Number.isNaN(Number(inputs.consignee_id)) ? Number(inputs.consignee_id) : null,
            customer_name: selectedCustomerOption?.label || customers.find((c) => String(c.value) === String(inputs.customer_id))?.label || '',
            consignee_name: selectedSiteOption?.label || sites.find((s) => String(s.value) === String(inputs.consignee_id))?.label || '',
            lines: computedLines.map((line, i) => ({
                line_no: i + 1,
                shape_type: line.shape_type,
                item_name: line.item_name,
                volume_m3: line.volume_m3,
                concrete_grade: line.concrete_grade,
                user_remarks: line.user_remarks,
            })),
        };

        getAllProducts(cookies)
            .then((res) => navigateToQuotationFromEstimateData(navigate, estimatePayload, res.product_list || []))
            .catch((err) => displayError(err?.response?.data ?? err.message, 'Convert failed'));
    };

    const openEstimatePdf = (id, pdfType) => (
        printConcreteEstimator(cookies, id, pdfType)
            .then((res) => openPdfResponse(res, { title: estimatePdfTitle(pdfType) }))
            .catch((err) => displayError(err?.response?.data ?? err.message, 'Print failed'))
    );

    const handlePrint = () => {
        if (!editId) return;
        chooseEstimatePdfType(inputs.client_requirement || 'qty').then((pdfType) => {
            if (pdfType) {
                openEstimatePdf(editId, pdfType);
            }
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const projectName = selectedSiteOption?.label?.trim() || inputs.project_name?.trim();
        if (!inputs.plant_id || !projectName) {
            Swal.fire('Validation', 'Plant and Site / Project are required.', 'warning');
            return;
        }
        const payload = {
            ...inputs,
            project_name: projectName,
            plant_id: Number(inputs.plant_id),
            estimate_date: formatDateForApi(inputs.estimate_date),
            customer_id: inputs.customer_id && !Number.isNaN(Number(inputs.customer_id)) ? Number(inputs.customer_id) : null,
            consignee_id: inputs.consignee_id && !Number.isNaN(Number(inputs.consignee_id)) ? Number(inputs.consignee_id) : null,
            distance_one_way_km: inputs.distance_one_way_km === '' ? 0 : Number(inputs.distance_one_way_km),
            distance_round_trip_km: inputs.distance_round_trip_km === '' ? 0 : Number(inputs.distance_round_trip_km),
            current_diesel_rate: inputs.current_diesel_rate === '' ? 0 : Number(inputs.current_diesel_rate),
            client_requirement: inputs.client_requirement || 'qty',
            rate_working: rateWorking,
            lines: lines.map((line, i) => ({
                line_no: i + 1,
                shape_type: line.shape_type,
                item_name: line.item_name,
                quantity: Number(line.quantity) || 1,
                concrete_grade: line.concrete_grade || 'M20',
                wastage_percent: Number(line.wastage_percent ?? 3),
                dimensions: summaryOverridesForDimensions(line.dimensions, line),
                manual_qty_m3: line.manual_qty_m3 === '' ? 0 : Number(line.manual_qty_m3) || 0,
                manual_rate_per_m3: line.manual_rate_per_m3 === '' ? 0 : Number(line.manual_rate_per_m3) || 0,
                user_remarks: line.user_remarks,
            })),
        };
        Swal.fire({
            title: editId ? 'Update estimate?' : 'Save estimate?',
            showCancelButton: true,
            confirmButtonText: 'Yes',
        }).then((result) => {
            if (!result.isConfirmed) return;
            setLoading(true);
            const req = editId
                ? updateConcreteEstimator(cookies, editId, payload)
                : createConcreteEstimator(cookies, payload);
            req.then((res) => {
                const savedId = res.id || editId;
                return Swal.fire({
                    icon: 'success',
                    title: 'Saved',
                    text: 'Generate PDF for the client now, or later after adding recipe / rate.',
                    input: 'radio',
                    inputOptions: ESTIMATE_PDF_TYPES,
                    inputValue: payload.client_requirement || 'qty',
                    showCancelButton: true,
                    confirmButtonText: 'Generate PDF',
                    cancelButtonText: 'Later',
                }).then((pdfChoice) => {
                    if (pdfChoice.isConfirmed && pdfChoice.value && savedId) {
                        return openEstimatePdf(savedId, pdfChoice.value);
                    }
                    return null;
                }).then(() => navigate('/ConcreteEstimatorTable'));
            })
                .catch((err) => displayError(err?.response?.data ?? err.message, 'Save failed'))
                .finally(() => setLoading(false));
        });
    };

    return (
        <div id="outer-container" className="App">
            <Sidebar pageWrapId="page-wrap" outerContainerId="outer-container" />
            <div id="page-wrap">
                <LoadingOverlay active={loading} />
                <ErrorToast />
                <FloatingControls
                    tableLink="/ConcreteEstimatorTable"
                    formLink="/ConcreteEstimator"
                    addNewLabel="New Estimate"
                    homeLink="/Home"
                />
                <div className="container">
                    <FormPageLayout
                        moduleLabel="UTILITY"
                        title={editId ? 'Edit Concrete Estimator' : 'Concrete Estimator'}
                        subtitle="Save quantity now. Add recipe and rate calculator later. Generate Concrete Qty Estimate, Concrete Rate Estimate, or Both for the client."
                        modeLabel={editId ? 'EDIT MODE' : 'CREATE MODE'}
                    >
                        <form onSubmit={handleSubmit}>
                            <Row>
                                <Col xl={6} lg={12} md={12}>
                                    <div className="form-row table-bordered shadow p-2 mb-3 form-control-panel">
                                        <label htmlFor="plant_id" className="form-group col-sm-4 text-right mandatory">Plant</label>
                                        <select id="plant_id" name="plant_id" className="form-control col-sm-7 mandatory-form-control browser-default custom-select" value={inputs.plant_id} onChange={(e) => {
                                            setDieselRateTouched(false);
                                            setInputs((v) => ({
                                                ...v,
                                                plant_id: e.target.value,
                                                estimate_no: '',
                                                prefix: '',
                                                current_diesel_rate: '',
                                                grn_diesel_rate: '',
                                            }));
                                        }} required>
                                            <option value="">Select</option>
                                            {plants.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                                        </select>
                                        <br />

                                        <label htmlFor="customer_id" className="form-group col-sm-4 text-right">Customer</label>
                                        <CreatableSelect
                                            inputId="customer_id"
                                            options={customers}
                                            isClearable
                                            isSearchable
                                            value={selectedCustomerOption}
                                            onChange={onCustomerSelect}
                                            className="react-select-dropdown col-sm-7"
                                            placeholder="Select from list or type name..."
                                            formatCreateLabel={(input) => `Use "${input}"`}
                                            noOptionsMessage={() => 'Type to add a customer name'}
                                        />
                                        <br />

                                        <label htmlFor="consignee_id" className="form-group col-sm-4 text-right mandatory">Site / Project</label>
                                        <CreatableSelect
                                            inputId="consignee_id"
                                            options={sites}
                                            isClearable
                                            isSearchable
                                            value={selectedSiteOption}
                                            onChange={onSiteSelect}
                                            className="react-select-dropdown col-sm-7 mandatory-form-control"
                                            placeholder="Same as customer, or select / type site..."
                                            formatCreateLabel={(input) => `Use "${input}"`}
                                            noOptionsMessage={() => 'Type to add a site / project name'}
                                        />
                                        <br />

                                        <label htmlFor="project_type" className="form-group col-sm-4 text-right">Project Type</label>
                                        <select id="project_type" name="project_type" className="form-control col-sm-7 browser-default custom-select" value={inputs.project_type} onChange={handleInput}>
                                            {PROJECT_TYPES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                        </select>
                                        <br />

                                        <label className="form-group col-sm-4 text-right">Client Requirement</label>
                                        <div className="col-sm-7 estimator-client-requirement">
                                            {Object.entries(ESTIMATE_PDF_TYPES).map(([value, label]) => (
                                                <label key={value} className="d-inline-flex align-items-center" style={{ gap: 4 }}>
                                                    <input
                                                        type="radio"
                                                        name="client_requirement"
                                                        value={value}
                                                        checked={(inputs.client_requirement || 'qty') === value}
                                                        onChange={handleInput}
                                                    />
                                                    {label}
                                                </label>
                                            ))}
                                        </div>
                                        <br />

                                        <label htmlFor="site_pincode" className="form-group col-sm-4 text-right">Pincode</label>
                                        <input
                                            id="site_pincode"
                                            name="site_pincode"
                                            className="form-control col-sm-7"
                                            value={inputs.site_pincode}
                                            onChange={handlePincodeChange}
                                            maxLength={6}
                                            placeholder="Site pincode"
                                        />
                                        <br />

                                        <label htmlFor="site_location" className="form-group col-sm-4 text-right">Site Location</label>
                                        <div className="col-sm-7 px-0 d-flex estimator-location-field">
                                            <select
                                                id="site_location"
                                                name="site_location"
                                                className="form-control browser-default custom-select"
                                                value={inputs.site_location}
                                                onChange={onSiteLocationSelect}
                                                disabled={!PINCODE_PATTERN.test(String(inputs.site_pincode || '').trim())}
                                            >
                                                <option value="">
                                                    {PINCODE_PATTERN.test(String(inputs.site_pincode || '').trim())
                                                        ? 'Select postal area'
                                                        : 'Enter pincode first'}
                                                </option>
                                                {locationOptions.map((opt) => (
                                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                ))}
                                                {inputs.site_location && !locationOptions.some((opt) => opt.value === inputs.site_location) ? (
                                                    <option value={inputs.site_location}>{inputs.site_location}</option>
                                                ) : null}
                                            </select>
                                            <Button
                                                type="button"
                                                color="secondary"
                                                size="sm"
                                                className="flex-shrink-0"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    openLocationMap();
                                                }}
                                                title="Open map and select location"
                                            >
                                                Map
                                            </Button>
                                        </div>
                                        <br />

                                        <label htmlFor="site_address" className="form-group col-sm-4 text-right">Site Address</label>
                                        {streetOptions.length || streetsLoading ? (
                                            <select
                                                id="site_address"
                                                name="site_address"
                                                className="form-control col-sm-7 browser-default custom-select"
                                                value={inputs.site_address}
                                                onChange={onSiteAddressSelect}
                                                disabled={!String(inputs.site_location || '').trim() || streetsLoading}
                                            >
                                                <option value="">
                                                    {streetsLoading ? 'Loading streets...' : 'Select street'}
                                                </option>
                                                {streetOptions.map((opt) => (
                                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                ))}
                                                {inputs.site_address && !streetOptions.some((opt) => opt.value === inputs.site_address) ? (
                                                    <option value={inputs.site_address}>{inputs.site_address}</option>
                                                ) : null}
                                            </select>
                                        ) : (
                                            <input
                                                id="site_address"
                                                name="site_address"
                                                className="form-control col-sm-7"
                                                value={inputs.site_address}
                                                onChange={handleInput}
                                                disabled={!String(inputs.site_location || '').trim()}
                                                placeholder={String(inputs.site_location || '').trim() ? 'Enter street / address' : 'Select location first'}
                                            />
                                        )}
                                    </div>
                                </Col>
                                <Col xl={6} lg={12} md={12}>
                                    <div className="form-row table-bordered shadow p-2 mb-3 form-control-panel">
                                        <label className="form-group col-sm-4 text-right">Estimate No</label>
                                        <input className="form-control col-sm-7" readOnly value={`${inputs.prefix || ''}${inputs.estimate_no || ''}`} />
                                        <br />

                                        <label htmlFor="estimate_date" className="form-group col-sm-4 text-right mandatory">Date</label>
                                        <input id="estimate_date" type="text" name="estimate_date" className="form-control col-sm-7 mandatory-form-control" value={inputs.estimate_date} onChange={(e) => {
                                            handleInput(e);
                                            setInputs((v) => ({ ...v, estimate_no: '', prefix: '' }));
                                        }} placeholder="DD-MM-YYYY" required />
                                        <br />

                                        <label htmlFor="distance_one_way_km" className="form-group col-sm-4 text-right">Distance (1 way) km</label>
                                        <input
                                            id="distance_one_way_km"
                                            type="number"
                                            step="any"
                                            min="0"
                                            className="form-control col-sm-7"
                                            value={inputs.distance_one_way_km}
                                            onChange={handleOneWayKm}
                                            placeholder="From pincode / site"
                                        />
                                        <br />

                                        <label htmlFor="distance_round_trip_km" className="form-group col-sm-4 text-right">Up &amp; Down km</label>
                                        <input
                                            id="distance_round_trip_km"
                                            type="number"
                                            step="any"
                                            min="0"
                                            name="distance_round_trip_km"
                                            className="form-control col-sm-7"
                                            value={inputs.distance_round_trip_km}
                                            onChange={handleInput}
                                            placeholder="Auto = 2 × one way"
                                        />
                                        <br />

                                        <label htmlFor="user_remarks" className="form-group col-sm-4 text-right">Remarks</label>
                                        <input id="user_remarks" name="user_remarks" className="form-control col-sm-7" value={inputs.user_remarks} onChange={handleInput} />
                                    </div>
                                </Col>
                            </Row>

                            <h5 className="mb-2">Calculation Lines</h5>
                            {lines.map((line, index) => {
                                const calc = computedLines[index] || {};
                                const fields = getShapeFieldLabels(line.shape_type);
                                return (
                                    <div key={index} className="estimator-calc-line mb-3">
                                        <div className="estimator-calc-line-header">
                                            <div className="estimator-calc-line-header-field">
                                                <label className="mandatory">Shape / Element</label>
                                                <select className="form-control estimator-calc-select" value={line.shape_type} onChange={(e) => changeShape(index, e.target.value)}>
                                                    {SHAPE_TYPES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                                                </select>
                                            </div>
                                            <div className="estimator-calc-line-header-field estimator-calc-line-header-field--wide">
                                                <label>Item Description</label>
                                                <input className="form-control estimator-calc-input" value={line.item_name} onChange={(e) => updateLine(index, { item_name: e.target.value })} placeholder="e.g. Ground floor slab" />
                                            </div>
                                            <div className="estimator-calc-line-header-field estimator-calc-line-header-field--measurement">
                                                <label>Measurement</label>
                                                <input
                                                    className="form-control estimator-calc-input estimator-calc-measurement"
                                                    readOnly
                                                    tabIndex={-1}
                                                    value={formatMeasurementSummary(line.shape_type, line.dimensions, line.quantity)}
                                                    placeholder="Enter dimensions below"
                                                    title={formatMeasurementSummary(line.shape_type, line.dimensions, line.quantity) || 'Updates from dimension inputs'}
                                                />
                                            </div>
                                            <div className="estimator-calc-line-header-field estimator-calc-line-header-field--volume">
                                                <label>Volume (m³)</label>
                                                <input
                                                    className="form-control estimator-calc-input estimator-calc-measurement"
                                                    readOnly
                                                    tabIndex={-1}
                                                    value={(calc.volume_m3 || 0) > 0 ? (calc.volume_m3 || 0).toFixed(3) : ''}
                                                    placeholder="—"
                                                    title={`Wet volume: ${(calc.volume_m3 || 0).toFixed(3)} m³`}
                                                />
                                            </div>
                                            <div className="estimator-calc-line-header-field">
                                                <label>Concrete Grade</label>
                                                <select
                                                    className="form-control estimator-calc-select"
                                                    value={line.concrete_grade || 'M20'}
                                                    onChange={(e) => updateLine(index, { concrete_grade: e.target.value })}
                                                >
                                                    {CONCRETE_GRADES.map((g) => (
                                                        <option key={g.value} value={g.value}>{g.label}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="estimator-calc-line-header-field estimator-calc-line-header-field--narrow">
                                                <label>Wastage %</label>
                                                <input
                                                    type="number"
                                                    step="0.1"
                                                    min="0"
                                                    className="form-control estimator-calc-input"
                                                    value={line.wastage_percent ?? 3}
                                                    onChange={(e) => updateLine(index, { wastage_percent: e.target.value })}
                                                />
                                            </div>
                                            <Button type="button" color="danger" size="sm" className="estimator-calc-remove" onClick={() => removeLine(index)} disabled={lines.length <= 1}>Remove</Button>
                                        </div>

                                        <div className="estimator-calc-body">
                                            <div className="estimator-calc-fields">
                                                {fields.map((f) => (
                                                    <div className="estimator-calc-row" key={f.key}>
                                                        <label className="estimator-calc-label" htmlFor={`line-${index}-${f.key}`}>{f.label}</label>
                                                        <input
                                                            id={`line-${index}-${f.key}`}
                                                            type="number"
                                                            step="any"
                                                            min={f.isCount ? '1' : undefined}
                                                            className="estimator-calc-input"
                                                            value={line.dimensions[f.key] ?? ''}
                                                            onChange={(e) => updateLineDim(index, f.key, e.target.value)}
                                                        />
                                                        {f.hasUnit ? (
                                                            <select
                                                                className="estimator-calc-unit"
                                                                value={line.dimensions[`${f.key}_unit`] || 'm'}
                                                                onChange={(e) => updateLineDim(index, `${f.key}_unit`, e.target.value)}
                                                            >
                                                                {LENGTH_UNITS.map((u) => (
                                                                    <option key={u.value} value={u.value}>{u.label}</option>
                                                                ))}
                                                            </select>
                                                        ) : (
                                                            <span className="estimator-calc-unit-spacer" />
                                                        )}
                                                    </div>
                                                ))}
                                                <div className="estimator-calc-row">
                                                    <label className="estimator-calc-label" htmlFor={`line-${index}-qty`}>Quantity</label>
                                                    <input
                                                        id={`line-${index}-qty`}
                                                        type="number"
                                                        min="1"
                                                        step="1"
                                                        className="estimator-calc-input"
                                                        value={line.quantity}
                                                        onChange={(e) => updateLine(index, { quantity: e.target.value })}
                                                    />
                                                    <span className="estimator-calc-unit-spacer" />
                                                </div>

                                                <div className="estimator-calc-actions">
                                                    <span className="estimator-calc-calc-btn" title="Volume updates automatically as you type">
                                                        ▶ Calculate
                                                    </span>
                                                    <button type="button" className="estimator-calc-clear-btn" onClick={() => clearLine(index)}>Clear</button>
                                                </div>

                                                <div className="estimator-calc-results">
                                                    <span>Volume: <strong>{(calc.volume_m3 || 0).toFixed(3)} m³</strong></span>
                                                </div>
                                            </div>
                                            <EstimatorShapeDiagram shapeType={line.shape_type} dimensions={line.dimensions} />
                                        </div>
                                    </div>
                                );
                            })}
                            <Button type="button" color="secondary" size="sm" onClick={addLine} className="mb-3">+ Add Line</Button>

                            {(inputs.client_requirement || 'qty') !== 'qty' ? (
                                <ConcreteEstimatorRateWorking
                                    cookies={cookies}
                                    plantId={inputs.plant_id}
                                    customerId={inputs.customer_id || ''}
                                    consigneeId={inputs.consignee_id || ''}
                                    lines={computedLines}
                                    totals={totals}
                                    distanceRoundTripKm={distanceRoundTripKm}
                                    currentDieselRate={inputs.current_diesel_rate === '' ? 0 : Number(inputs.current_diesel_rate) || 0}
                                    grnDieselRate={inputs.grn_diesel_rate === '' ? 0 : Number(inputs.grn_diesel_rate) || 0}
                                    onDieselRateChange={(rate) => {
                                        setDieselRateTouched(true);
                                        setInputs((v) => ({
                                            ...v,
                                            current_diesel_rate: rate === '' || rate == null ? '' : String(rate),
                                        }));
                                    }}
                                    editId={editId}
                                    initialSheet={initialRateSheet}
                                    onChange={handleRateWorkingChange}
                                />
                            ) : (
                                <p className="text-muted mb-3">
                                    Recipe and rate calculator can be added later. Change Client Requirement to Rate or Both, then edit this estimate.
                                </p>
                            )}

                            <Table bordered size="sm" className="mb-3">
                                <thead className="thead-light">
                                    <tr>
                                        <th>Shape</th>
                                        <th>Measurement</th>
                                        <th>Grade</th>
                                        <th>Unit</th>
                                        <th className="text-right">Qty</th>
                                        <th className="text-right">Rate</th>
                                        <th className="text-right">Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {computedLines.map((line, i) => {
                                        const sourceLine = lines[i] || line;
                                        const lineQty = getSummaryLineQty(sourceLine, line);
                                        const lineRate = getSummaryLineRate(sourceLine, defaultRatePerM3);
                                        const lineAmount = lineQty * lineRate;
                                        const measurementText = formatMeasurementSummary(
                                            line.shape_type,
                                            sourceLine.dimensions,
                                            sourceLine.quantity,
                                        );
                                        const qtyInputValue = sourceLine.manual_qty_m3 !== ''
                                            ? sourceLine.manual_qty_m3
                                            : (Number(line.volume_m3) > 0 ? Number(line.volume_m3).toFixed(3) : '');
                                        const rateInputValue = sourceLine.manual_rate_per_m3 !== ''
                                            ? sourceLine.manual_rate_per_m3
                                            : (defaultRatePerM3 > 0 ? String(defaultRatePerM3) : '');
                                        return (
                                            <tr key={i}>
                                                <td>{SHAPE_TYPES.find((s) => s.value === line.shape_type)?.label || '—'}</td>
                                                <td className="estimator-measurement-cell">
                                                    {measurementText || '—'}
                                                </td>
                                                <td>{line.concrete_grade || 'M20'}</td>
                                                <td>m³</td>
                                                <td className="text-right">
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        step="0.001"
                                                        className="form-control form-control-sm text-right estimator-summary-qty-input"
                                                        value={qtyInputValue}
                                                        onChange={(e) => updateLineManualQty(i, e.target.value)}
                                                    />
                                                </td>
                                                <td className="text-right">
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        step="1"
                                                        className="form-control form-control-sm text-right estimator-summary-rate-input"
                                                        value={rateInputValue}
                                                        onChange={(e) => updateLineManualRate(i, e.target.value)}
                                                    />
                                                </td>
                                                <td className="text-right">
                                                    {lineQty > 0 && lineRate > 0 ? formatCurrency(lineAmount) : '—'}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                                <tfoot>
                                    <tr className="font-weight-bold">
                                        <td colSpan={4} className="text-right">Project Total</td>
                                        <td className="text-right">{summaryTotals.totalQty.toFixed(3)}</td>
                                        <td className="text-right">
                                            {summaryTotals.avgRate > 0 ? formatRatePerM3(summaryTotals.avgRate) : '—'}
                                        </td>
                                        <td className="text-right">
                                            {summaryTotals.totalAmount > 0
                                                ? formatCurrency(summaryTotals.totalAmount)
                                                : '—'}
                                        </td>
                                    </tr>
                                </tfoot>
                            </Table>

                            <div className="text-center mb-4">
                                <Button type="submit" color="success" style={{ minWidth: 120 }}>{editId ? 'Update' : 'Save'}</Button>
                                {' '}
                                {editId && (
                                    <>
                                        <Button type="button" color="info" style={{ minWidth: 120 }} onClick={handlePrint}>
                                            Print PDF
                                        </Button>
                                        {' '}
                                    </>
                                )}
                                <Button type="button" color="primary" style={{ minWidth: 160 }} onClick={handleConvertToQuotation}>
                                    Convert to Quotation
                                </Button>
                            </div>
                        </form>
                    </FormPageLayout>
                </div>
            </div>
            <EstimatorLocationMapModal
                open={mapOpen}
                title="Shortest route: Plant → Site"
                plant={plantCoords}
                site={siteCoords}
                selectedLabel={inputs.site_location}
                onClose={closeLocationMap}
                onSelect={handleMapSelect}
            />
        </div>
    );
}

export default function ConcreteEstimator() {
    return <ConcreteEstimatorForm />;
}

export function ConcreteEstimatorEdit() {
    const { id } = useParams();
    return <ConcreteEstimatorForm editId={id} />;
}
